/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ColumnsType } from 'antd/es/table';

import { GetSubtenantMembersResponse, Subtenant, UpdateSubtenantMembership, User } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useUpdateSubtenantMembership } from '@/hooks/mutation/useUpdateSubtenantMembership';
import { usePaginatedSubtenantMembersQuery } from '@/hooks/query/usePaginatedSubtenantMembersQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Select } from '@/primitives/Select';
import { Title } from '@/primitives/Title';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type SubtenantMembersProps = {
  subtenant: Subtenant;
  membersQuery: ReturnType<typeof usePaginatedSubtenantMembersQuery>;
};

export function SubtenantMembers({ subtenant, membersQuery }: SubtenantMembersProps) {
  const { t } = useMimicTranslation('subtenants');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [message] = useMessage();

  const { mutate } = useUpdateSubtenantMembership({
    onError: () => {
      message.error(t('errors.unableToUpdateMembership'));
    },
  });

  const onRoleChange = (userId: string, role: UpdateSubtenantMembership['role']) => {
    mutate({ id: subtenant.id, userId, tenantId: tenantId!, requestBody: { role } });
  };

  const activeUsersColumns: ColumnsType<User> = [
    {
      title: t('user'),
      dataIndex: 'displayName',
      key: 'displayName',
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: User['role'], user) => {
        return (
          <Select
            value={role}
            style={{ width: 200 }}
            onChange={(value) => {
              onRoleChange(user.id, value as UpdateSubtenantMembership['role']);
            }}
            options={[
              { value: 'inherit', label: t('roles.inherit') },
              { value: 'admin', label: t('roles.admin') },
              { value: 'editor', label: t('roles.editor') },
              { value: 'viewer', label: t('roles.viewer') },
              { value: 'none', label: t('roles.noAccess') },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Container style={{ marginTop: '24px' }}>
      <Title level={3}>{t('members')}</Title>
      <QueryWrapper<GetSubtenantMembersResponse> queryResult={membersQuery}>
        {(data) => (
          <PaginatedTable
            data={data}
            columns={activeUsersColumns}
            rowKey={(user: User) => user.id}
            onParamsChange={membersQuery.updateQueryParams}
            style={{ width: '100%' }}
          />
        )}
      </QueryWrapper>
    </Container>
  );
}
